import { WAIT_COLOR, WAIT_TOLERANCE_MINUTES, roundedMinutes, shortClockTime, waitColor } from '@/utils/kitchenWaitCounter'
import { WAIT_STATE } from './kitchenWaitConstants.js'
import { LIVE_WAIT_LABEL, LIVE_WAIT_TEXT, LIVE_WAIT_TONE } from './liveWaitStatusTexts.js'

const MINIMUM_WAIT_SECONDS = 0

const NOT_COUNTED_TEXT_BY_STATE = {
  [WAIT_STATE.BLOCKED_BY_MV]: LIVE_WAIT_TEXT.BLOCKED_BY_MV,
  [WAIT_STATE.ORDER_ADDED]: LIVE_WAIT_TEXT.ORDER_ADDED,
  [WAIT_STATE.MANUAL_ARRIVAL]: LIVE_WAIT_TEXT.MANUAL_ARRIVAL,
  [WAIT_STATE.DISCARDED_WAIT]: LIVE_WAIT_TEXT.DISCARDED_WAIT
}

const STARTED_STATES = [WAIT_STATE.STARTED, WAIT_STATE.DISCARDED_WAIT]

const LABEL_BY_COLOR = {
  [WAIT_COLOR.GREEN]: LIVE_WAIT_LABEL.ON_TIME,
  [WAIT_COLOR.ORANGE]: LIVE_WAIT_LABEL.ATTENTION,
  [WAIT_COLOR.RED]: LIVE_WAIT_LABEL.DELAYED
}

function buildStatus (label, tone, explanation) {
  return { label, tone, explanation }
}

function buildNotCounted (explanation) {
  return buildStatus(LIVE_WAIT_LABEL.NOT_COUNTED, LIVE_WAIT_TONE.NOT_COUNTED, explanation)
}

function excessMinutes (seconds) {
  return roundedMinutes(seconds) - WAIT_TOLERANCE_MINUTES
}

function describeWaiting (seconds) {
  const color = waitColor(seconds)
  const minutes = roundedMinutes(seconds)
  const explanations = {
    [WAIT_COLOR.GREEN]: LIVE_WAIT_TEXT.waitingWithinTolerance(minutes),
    [WAIT_COLOR.ORANGE]: LIVE_WAIT_TEXT.waitingInAttention(minutes),
    [WAIT_COLOR.RED]: LIVE_WAIT_TEXT.waitingOverTolerance(minutes, excessMinutes(seconds))
  }

  return buildStatus(LABEL_BY_COLOR[color], color, explanations[color])
}

function leftBeforeSchedule (route) {
  return Boolean(route.scheduled_departure_time) && route.route_started_time < route.scheduled_departure_time
}

function isStartedSecondTrip (route) {
  return STARTED_STATES.includes(route.state) && Boolean(route.first_round_route)
}

function withoutNegativeWait (seconds) {
  return Math.max(MINIMUM_WAIT_SECONDS, seconds)
}

function describeStarted (route) {
  const seconds = withoutNegativeWait(route.wait_seconds)
  const isDelayed = waitColor(seconds) === WAIT_COLOR.RED
  const minutes = roundedMinutes(seconds)
  const waited = isDelayed
    ? LIVE_WAIT_TEXT.waitedOverTolerance(minutes, excessMinutes(seconds))
    : LIVE_WAIT_TEXT.waitedWithinTolerance(minutes)
  const explanation = leftBeforeSchedule(route) ? `${waited} ${LIVE_WAIT_TEXT.LEFT_EARLY}` : waited

  return isDelayed
    ? buildStatus(LIVE_WAIT_LABEL.DELAYED, WAIT_COLOR.RED, explanation)
    : buildStatus(LIVE_WAIT_LABEL.ON_TIME, WAIT_COLOR.GREEN, explanation)
}

export function describeLiveWaitStatus (route, liveSeconds) {
  if (route.state === WAIT_STATE.WAITING_ARRIVAL) {
    return buildStatus(LIVE_WAIT_LABEL.NO_ARRIVAL, LIVE_WAIT_TONE.NO_ARRIVAL, null)
  }
  if (route.state === WAIT_STATE.SECOND_TRIP || isStartedSecondTrip(route)) {
    return buildNotCounted(LIVE_WAIT_TEXT.secondTrip(route.first_round_route))
  }
  if (NOT_COUNTED_TEXT_BY_STATE[route.state]) {
    return buildNotCounted(NOT_COUNTED_TEXT_BY_STATE[route.state])
  }
  if (route.state === WAIT_STATE.BEFORE_SCHEDULE) {
    return buildStatus(LIVE_WAIT_LABEL.ON_TIME, WAIT_COLOR.GREEN, LIVE_WAIT_TEXT.beforeSchedule(shortClockTime(route.scheduled_departure_time)))
  }
  if (route.state === WAIT_STATE.STARTED) {
    return describeStarted(route)
  }

  return describeWaiting(liveSeconds)
}
